import { ObjectConfig, ObjectState, required } from "@homebound/form-state";
import { SaveChangeEventInput } from "src/generated/graphqlTypes";
import { GuidedChangeEventPage_ChangeEventFragment } from "src/routes/projects/change-events/guided/GuidedChangeEventPage.generated";

/**
 * The change-event fields every guided workflow carries, whether it's creating the draft or editing
 * one: what the `CreateGuidedChangeEventModal` collects, plus what the Review step edits.
 * Each workflow spreads this into its own `formConfig`, so the draft is described in exactly one place.
 */
export type BaseChangeEventFormInput = Pick<
  SaveChangeEventInput,
  | "id"
  | "workflow"
  | "projectStageId"
  | "type"
  | "title"
  | "internalNote"
  | "reasonId"
  | "hasPreSignedContract"
  | "documentId"
> & {
  /** Display-only: the attached file name for hasPreSignedContract, so the Review step can show what `documentId` points at. */
  documentName?: string | null;
  /**
   * The id of a dry-run change event (`saveChangeEvent` with `dryRun: true`), which only exists in Apollo's cache. It
   * lets the steps preview a change event that will never be saved, i.e. applying a configuration with no signed
   * contract, without becoming the form's `id`: it is never sent to the server and never means "the draft exists".
   */
  dryRunChangeEventId?: string | null;
};

export type BaseChangeEventFormState<F> = ObjectState<F> & ObjectState<BaseChangeEventFormInput>;

export const baseChangeEventFormConfig: ObjectConfig<BaseChangeEventFormInput> = {
  // Unset until the first step creates the draft. Not `readOnly`, so a workflow can clear it to re-create the draft.
  id: { type: "value" },
  workflow: { type: "value" },
  type: { type: "value" },
  projectStageId: { type: "value", readOnly: true },
  title: { type: "value" },
  reasonId: { type: "value", rules: [required] },
  internalNote: { type: "value" },
  hasPreSignedContract: { type: "value" },
  documentId: { type: "value" },
  documentName: { type: "value", isLocalOnly: true },
  dryRunChangeEventId: { type: "value", isLocalOnly: true },
};

/**
 * Maps the shared change-event fields back into the form. Each workflow's `mapToForm` spreads this and then adds its
 * own workflow-specific fields on top.
 */
export function baseChangeEventMapToForm(
  changeEvent: GuidedChangeEventPage_ChangeEventFragment,
): BaseChangeEventFormInput {
  return {
    id: changeEvent.id,
    workflow: changeEvent.workflow?.code,
    projectStageId: changeEvent.projectStage.id,
    type: changeEvent.type.code,
    title: changeEvent.title,
    internalNote: changeEvent.internalNote,
    reasonId: changeEvent.reason.id,
    hasPreSignedContract: changeEvent.hasPreSignedContract,
    documentId: changeEvent.document?.id,
    documentName: changeEvent.document?.name,
  };
}
